"use client";

import * as React from "react";
import { Controller, type FieldValues } from "react-hook-form";
import clsx from "clsx";
import type { SxProps, Theme } from "@mui/material/styles";
import type { FormDateRangeFieldsProps, ISODateOnly, RangeValue } from "./formDateRangeFields.types";
import { cmpISO, toISOOrNull, withRequiredAsterisk } from "../utils";
import { SharedDateInput, type SharedDateInputProps } from "../sharedDateInput";
import { DateDisplayFormat } from "../types";

const DEFAULT_ACTIVE_SELECTION_COLOR = "var(--mui-palette-warning-main)";

const criarSxSelecaoAtiva = (color: string): SxProps<Theme> => ({
	"& .MuiFormLabel-root": { color: `${color} !important` },
	"& .MuiInputLabel-root": { color: `${color} !important` },
	"& .MuiInputLabel-root.Mui-focused": { color: `${color} !important` },
	"& .MuiOutlinedInput-notchedOutline": { borderColor: `${color} !important` },
	"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: `${color} !important` },
	"& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: `${color} !important` },
	"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: `${color} !important` },
});

function normalizeRange(value: unknown, emptyValue: "" | null): RangeValue {
	if (Array.isArray(value)) return [value[0] ?? emptyValue, value[1] ?? emptyValue];
	return [emptyValue, emptyValue];
}

function mesclarSxCampo(sxBase?: SxProps<Theme>, sxAdicional?: SxProps<Theme>): SxProps<Theme> | undefined {
	if (!sxAdicional) return sxBase;
	if (!sxBase) return sxAdicional;

	return [
		...(Array.isArray(sxBase) ? sxBase : [sxBase]),
		...(Array.isArray(sxAdicional) ? sxAdicional : [sxAdicional]),
	] as SxProps<Theme>;
}

function DateInput({
	geralLabel,
	name,
	disabled,
	placeholder,
	displayFormat,
	textFieldProps,
	required,
	isoValue,
	minISO,
	maxISO,
	otherISO,
	onCommit,
	onCommitAlsoClearOther,
	rhfErrorMsg,
	fieldError,
	setFieldError,
	rangeRule,
	skipAuxiliaryActionsTab = true,
	reserveHelperText,
}: Readonly<SharedDateInputProps>) {
	return (
		<SharedDateInput
			geralLabel={geralLabel}
			name={name}
			disabled={disabled}
			placeholder={placeholder}
			displayFormat={displayFormat}
			textFieldProps={textFieldProps}
			required={required}
			isoValue={isoValue}
			minISO={minISO}
			maxISO={maxISO}
			otherISO={otherISO}
			rangeRule={rangeRule}
			onCommit={onCommit}
			onCommitAlsoClearOther={onCommitAlsoClearOther}
			rhfErrorMsg={rhfErrorMsg}
			fieldError={fieldError}
			setFieldError={setFieldError}
			ariaLabel={`Abrir calendário: ${name}`}
			skipAuxiliaryActionsTab={skipAuxiliaryActionsTab}
			reserveHelperText={reserveHelperText}
		/>
	);
}

type InnerProps = {
	field: any;
	fieldState: any;
	startLabel: React.ReactNode;
	endLabel: React.ReactNode;
	disabled?: boolean;
	emptyValue: "" | null;
	textFieldProps?: FormDateRangeFieldsProps<FieldValues>["textFieldProps"];
	fieldSx?: SxProps<Theme>;
	highlightActiveSelection?: boolean;
	activeHighlightColor?: string;
	geralLabel?: React.ReactNode;
	required?: boolean;
	displayFormat: DateDisplayFormat;
	className?: string;
	fieldsContainerClassName?: string;
	leadingElement?: React.ReactNode;
	reserveHelperText?: boolean;
	skipAuxiliaryActionsTab?: boolean;
};

function FormDateRangeFieldsInner({
	field,
	fieldState,
	startLabel,
	endLabel,
	disabled,
	emptyValue,
	textFieldProps,
	fieldSx,
	highlightActiveSelection = true,
	activeHighlightColor = DEFAULT_ACTIVE_SELECTION_COLOR,
	geralLabel,
	required,
	displayFormat,
	className,
	fieldsContainerClassName,
	leadingElement,
	reserveHelperText,
	skipAuxiliaryActionsTab = true,
}: Readonly<InnerProps>) {
	const [startRaw, endRaw] = normalizeRange(field.value, emptyValue);

	const startISO = toISOOrNull(startRaw);
	const endISO = toISOOrNull(endRaw);

	const rhfErrorMsg = fieldState.error?.message as string | undefined;

	const [startErr, setStartErr] = React.useState<unknown>(null);
	const [endErr, setEndErr] = React.useState<unknown>(null);

	const hasActiveRange = Boolean(startISO || endISO);
	const activeSelectionSx =
		highlightActiveSelection && hasActiveRange ? criarSxSelecaoAtiva(activeHighlightColor) : undefined;
	const mergedTextFieldProps = {
		...textFieldProps,
		sx: mesclarSxCampo(textFieldProps?.sx, mesclarSxCampo(fieldSx, activeSelectionSx)),
	};

	function commitStart(next: ISODateOnly | null) {
		if (!next) {
			field.onChange([emptyValue, endRaw]);
			return;
		}

		if (endISO && cmpISO(next, endISO) > 0) {
			field.onChange([next, emptyValue]);
			return;
		}

		field.onChange([next, endRaw]);
	}

	function commitEnd(next: ISODateOnly | null) {
		if (!next) {
			field.onChange([startRaw, emptyValue]);
			return;
		}

		if (startISO && cmpISO(next, startISO) < 0) {
			field.onChange([emptyValue, next]);
			return;
		}

		field.onChange([startRaw, next]);
	}

	const headerLabel = typeof geralLabel === "string" ? withRequiredAsterisk(geralLabel, required) : geralLabel;
	const showSubLabels = Boolean(geralLabel);

	let startInputLabel: React.ReactNode | undefined;
	if (showSubLabels)
		if (typeof startLabel === "string") startInputLabel = withRequiredAsterisk(startLabel, required);
		else startInputLabel = startLabel;

	let endInputLabel: React.ReactNode | undefined;
	if (showSubLabels)
		if (typeof endLabel === "string") endInputLabel = withRequiredAsterisk(endLabel, required);
		else endInputLabel = endLabel;

	return (
		<div className={clsx("flex flex-col", className)}>
			{geralLabel != null && <div className="text-text-secondary mb-1 text-sm font-semibold">{headerLabel}</div>}

			<div className={fieldsContainerClassName ?? "flex flex-row gap-2"}>
				{leadingElement}

				<DateInput
					geralLabel={startInputLabel}
					name={`${field.name}-start`}
					disabled={Boolean(disabled)}
					placeholder={displayFormat === "mm/yyyy" ? "mm/aaaa" : "dd/mm/aaaa"}
					displayFormat={displayFormat}
					required={required}
					textFieldProps={{
						...mergedTextFieldProps,
						onBlur: field.onBlur,
					}}
					isoValue={startISO}
					minISO={null}
					maxISO={endISO ?? null}
					otherISO={endISO}
					rangeRule="start"
					onCommit={(iso) => commitStart(iso)}
					onCommitAlsoClearOther={() => {
						field.onChange([startISO ?? emptyValue, emptyValue]);
					}}
					rhfErrorMsg={rhfErrorMsg}
					fieldError={startErr}
					setFieldError={setStartErr}
					skipAuxiliaryActionsTab={skipAuxiliaryActionsTab}
					reserveHelperText={reserveHelperText}
				/>

				<DateInput
					geralLabel={endInputLabel}
					name={`${field.name}-end`}
					disabled={Boolean(disabled)}
					placeholder={displayFormat === "mm/yyyy" ? "mm/aaaa" : "dd/mm/aaaa"}
					displayFormat={displayFormat}
					required={required}
					textFieldProps={{
						...mergedTextFieldProps,
						onBlur: field.onBlur,
					}}
					isoValue={endISO}
					minISO={startISO ?? null}
					maxISO={null}
					otherISO={startISO}
					rangeRule="end"
					onCommit={(iso) => commitEnd(iso)}
					onCommitAlsoClearOther={() => {
						field.onChange([emptyValue, endISO ?? emptyValue]);
					}}
					rhfErrorMsg={rhfErrorMsg}
					fieldError={endErr}
					setFieldError={setEndErr}
					skipAuxiliaryActionsTab={skipAuxiliaryActionsTab}
					reserveHelperText={reserveHelperText}
				/>
			</div>
		</div>
	);
}

export function FormDateRangeFields<TFieldValues extends FieldValues>({
	control,
	name,
	startLabel,
	endLabel,
	disabled,
	emptyValue = null,
	textFieldProps,
	fieldSx,
	highlightActiveSelection = true,
	activeHighlightColor = DEFAULT_ACTIVE_SELECTION_COLOR,
	geralLabel,
	required,
	displayFormat = "dd/mm/yyyy",
	className,
	fieldsContainerClassName,
	leadingElement,
	reserveHelperText = true,
	skipAuxiliaryActionsTab = true,
}: Readonly<FormDateRangeFieldsProps<TFieldValues>>) {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<FormDateRangeFieldsInner
					field={field}
					fieldState={fieldState}
					startLabel={startLabel}
					endLabel={endLabel}
					disabled={disabled}
					emptyValue={emptyValue}
					textFieldProps={textFieldProps}
					fieldSx={fieldSx}
					highlightActiveSelection={highlightActiveSelection}
					activeHighlightColor={activeHighlightColor}
					geralLabel={geralLabel}
					required={required}
					displayFormat={displayFormat}
					className={className}
					fieldsContainerClassName={fieldsContainerClassName}
					leadingElement={leadingElement}
					reserveHelperText={reserveHelperText}
					skipAuxiliaryActionsTab={skipAuxiliaryActionsTab}
				/>
			)}
		/>
	);
}
